/**
 * useHls.ts — хук инициализации hls.js плеера.
 *
 * Создаёт экземпляр Hls, настраивает ABR-параметры,
 * подключает к <video> элементу, управляет lifecycle.
 *
 * При выборе BOLA подключает хук-оверрайд (attachBola),
 * который переопределяет hls.nextAutoLevel после каждого фрагмента.
 */

import { useEffect, useRef, useState } from 'react';
import Hls, {
  type Events,
  type ManifestParsedData,
  type LevelSwitchedData,
  type ErrorData,
  type HlsConfig,
} from 'hls.js';
import type { HLSLevel, ABRAlgorithm } from '../types';
import { attachBola, type BolaHandle } from '../abr/BolaController';
import { getAuthToken } from '../api/client';

interface UseHlsResult {
  hls: Hls | null;
  levels: HLSLevel[];
  currentLevel: number;          // -1 = авто, 0..N = индекс уровня
  isSupported: boolean;
  error: string | null;
}

export function useHls(
  src: string | null,
  videoRef: React.RefObject<HTMLVideoElement>,
  abrAlgorithm: ABRAlgorithm = 'ewma'
): UseHlsResult {
  const hlsRef = useRef<Hls | null>(null);
  const bolaRef = useRef<BolaHandle | null>(null);
  // State для триггера ре-рендера при создании/уничтожении hls
  const [hlsInstance, setHlsInstance] = useState<Hls | null>(null);
  const [levels, setLevels] = useState<HLSLevel[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);

  const isSupported = Hls.isSupported();

  useEffect(() => {
    if (!src || !videoRef.current) return;
    const videoEl = videoRef.current;

    // Safari: нативная поддержка HLS
    if (!isSupported) {
      if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
        videoEl.src = src;
      } else {
        setError('HLS не поддерживается в этом браузере');
      }
      return;
    }

    // Уничтожаем предыдущий экземпляр
    if (bolaRef.current) {
      bolaRef.current.destroy();
      bolaRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
      setHlsInstance(null);
    }

    // BOLA (buffer-based) требует длинного буфера для корректной работы,
    // EWMA (throughput-based) работает лучше с коротким буфером.
    const isBola = abrAlgorithm === 'bola';

    const hlsConfig: Partial<HlsConfig> = {
      // ── ABR параметры ──────────────────────────────────────────────────
      abrEwmaDefaultEstimate: 5_000_000,  // начальная оценка 5 Mbps → старт с 720p
      abrBandWidthFactor:   0.90,         // выбор уровня (90% от оценки)
      abrBandWidthUpFactor: 0.85,         // консервативный подъём качества
      maxBufferLength:    isBola ? 30 : 18,
      maxMaxBufferLength: isBola ? 60 : 30,
      maxBufferSize: isBola ? 60 * 1000 * 1000 : 30 * 1000 * 1000,
      backBufferLength: 30,
      lowLatencyMode: false,
      startLevel: -1,                     // авто-старт
      enableWorker: true,
      debug: false,
      xhrSetup: (xhr) => {
        const token = getAuthToken();
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      },
    };

    const hls = new Hls(hlsConfig);
    hlsRef.current = hls;
    setError(null);

    // ── Подключаем BOLA-оверрайд поверх стандартного ABR ──
    if (isBola) {
      bolaRef.current = attachBola(hls);
    }

    hls.loadSource(src);
    hls.attachMedia(videoEl);

    // Манифест загружен → обновляем список уровней
    hls.on(Hls.Events.MANIFEST_PARSED, (_event: Events.MANIFEST_PARSED, data: ManifestParsedData) => {
      const hlsLevels: HLSLevel[] = data.levels.map((level) => ({
        height: level.height,
        width:  level.width,
        bitrate: level.bitrate,
        name: `${level.height}p`,
      }));
      setLevels(hlsLevels);
      setCurrentLevel(hls.currentLevel);
      setHlsInstance(hls);
    });

    // Переключение уровня
    hls.on(Hls.Events.LEVEL_SWITCHED, (_event: Events.LEVEL_SWITCHED, data: LevelSwitchedData) => {
      setCurrentLevel(data.level);
    });

    // Ошибки
    hls.on(Hls.Events.ERROR, (_event: Events.ERROR, data: ErrorData) => {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;
          default:
            setError(`HLS ошибка: ${data.details}`);
            hls.destroy();
            hlsRef.current = null;
            setHlsInstance(null);
            break;
        }
      }
    });

    return () => {
      if (bolaRef.current) {
        bolaRef.current.destroy();
        bolaRef.current = null;
      }
      hls.destroy();
      hlsRef.current = null;
      setHlsInstance(null);
    };
  }, [src, isSupported, abrAlgorithm]); // videoRef стабилен — не включаем

  return {
    hls: hlsInstance,
    levels,
    currentLevel,
    isSupported,
    error,
  };
}
